import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  BondDetail,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  BondInvestorCreateRequest,
  BondTradeBmkCreateRequest,
  BondTradeLeadBulkCreateRequest,
  BondTradeLeadCreateRequest,
  BondTradeLeadCreateResponse,
  BondTradeLeadUpdateRequest,
  BondDocumentItem,
  RelatedInstrumentItem,
  DwCustomerGroup,
  BondInvestorItem,
  BondNoteItem,
  BondTradeBmkItem,
  BondDetailUpsertRequest,
  BondInvestorResponse,
  BondInvestorUpdateRequest,
  BondTradeBmkResponse,
  BondTradeBmkUpdateRequest,
  NoteCreateRequest,
  NoteResponse,
  NoteUpdateRequest,
  NonNamedDealerItem,
  SimpleCatalogOption,
  TradeLeadItem,
} from './bond-detail.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondDetailService {
  private readonly endpoint = apiUrl('bond');
  private readonly documentsEndpoint = apiUrl('document');
  private readonly bondInvestorEndpoint = apiUrl('BondInvestor');
  private readonly bondTradeBmkEndpoint = apiUrl('BondTradeBmk');
  private readonly bondTradeLeadEndpoint = apiUrl('BondTradeLead');
  private readonly bondDealerEndpoint = apiUrl('BondDealer');
  private readonly dwCustomersEndpoint = apiUrl('DwCustomers');
  private readonly notesEndpoint = apiUrl('Notes');

  constructor(private readonly http: HttpClient) {}

  getBondDetail(tradeId: string): Observable<BondDetail> {
    return this.http.get<unknown>(`${this.endpoint}/${encodeURIComponent(tradeId)}`).pipe(
      map((response) => this.normalizeBondDetail(response)),
    );
  }

  getRelatedInstruments(tradeId: string, structureId: string): Observable<RelatedInstrumentItem[]> {
    const params = new HttpParams()
      .set('tradeId', tradeId.trim())
      .set('structureId', structureId.trim());

    return this.http.get<unknown>(`${this.endpoint}/related-instruments`, { params }).pipe(
      map((response) => this.normalizeRelatedInstruments(response)),
    );
  }

  deleteBond(tradeId: string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/${encodeURIComponent(tradeId)}`);
  }

  saveBondDetail(payload: BondDetailUpsertRequest): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/details`, payload);
  }

  createBondInvestor(payload: BondInvestorCreateRequest): Observable<BondInvestorResponse> {
    return this.http.post<BondInvestorResponse>(this.bondInvestorEndpoint, payload);
  }

  updateBondInvestor(tradeId: string, investorId: number, payload: BondInvestorUpdateRequest): Observable<void> {
    return this.http.put<void>(
      `${this.bondInvestorEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(String(investorId))}`,
      payload,
    );
  }

  deleteBondInvestor(tradeId: string, investorId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.bondInvestorEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(String(investorId))}`,
    );
  }

  createBondTradeBmk(payload: BondTradeBmkCreateRequest): Observable<BondTradeBmkResponse> {
    return this.http.post<BondTradeBmkResponse>(this.bondTradeBmkEndpoint, payload);
  }

  updateBondTradeBmk(tradeId: string, reofferId: number, payload: BondTradeBmkUpdateRequest): Observable<void> {
    return this.http.put<void>(
      `${this.bondTradeBmkEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(String(reofferId))}`,
      payload,
    );
  }

  deleteBondTradeBmk(tradeId: string, reofferId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.bondTradeBmkEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(String(reofferId))}`,
    );
  }

  getGroupedCustomers(legalName?: string): Observable<DwCustomerGroup[]> {
    const normalizedName = legalName?.trim() ?? '';
    const query = normalizedName
      ? `?legalName=${encodeURIComponent(normalizedName)}`
      : '';

    return this.http.get<unknown>(`${this.dwCustomersEndpoint}${query}`).pipe(
      map((response) => this.normalizeGroupedCustomers(response)),
    );
  }

  createBondTradeLead(payload: BondTradeLeadCreateRequest): Observable<BondTradeLeadCreateResponse> {
    return this.http.post<unknown>(this.bondTradeLeadEndpoint, payload).pipe(
      map((response) => this.normalizeBondTradeLeadResponse(response)),
    );
  }

  createBondTradeLeadsBulk(payload: BondTradeLeadBulkCreateRequest): Observable<BondTradeLeadCreateResponse[]> {
    return this.http.post<unknown>(`${this.bondTradeLeadEndpoint}/bulk`, payload).pipe(
      map((response) => {
        const record = this.toRecord(response);
        const items = Array.isArray(response) ? response : record['data'];
        return Array.isArray(items) ? items.map((item) => this.normalizeBondTradeLeadResponse(item)) : [];
      }),
    );
  }

  updateBondTradeLead(tradeId: string, customerId: string, payload: BondTradeLeadUpdateRequest): Observable<void> {
    return this.http.put<void>(
      `${this.bondTradeLeadEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(customerId)}`,
      payload,
    );
  }

  deleteBondTradeLead(tradeId: string, customerId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.bondTradeLeadEndpoint}/${encodeURIComponent(tradeId)}/${encodeURIComponent(customerId)}`,
    );
  }

  // Replaces the full set of non named dealers linked to a CDN trade.
  replaceBondDealers(tradeId: string, dealerIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.bondDealerEndpoint}/${encodeURIComponent(tradeId)}`, { dealerIds });
  }

  getNotes(): Observable<NoteResponse[]> {
    return this.http.get<unknown>(this.notesEndpoint).pipe(
      map((response) => this.normalizeNotes(response)),
    );
  }

  createNote(payload: NoteCreateRequest): Observable<NoteResponse> {
    return this.http.post<unknown>(this.notesEndpoint, payload).pipe(
      map((response) => this.normalizeNote(response)),
    );
  }

  updateNote(id: number, payload: NoteUpdateRequest): Observable<void> {
    return this.http.put<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`, payload);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`);
  }

  getDocumentsByTrade(tradeId: string): Observable<BondDocumentItem[]> {
    return this.http.get<unknown>(`${this.documentsEndpoint}/trade/${encodeURIComponent(tradeId)}`).pipe(
      map((response) => this.toDocuments(response)),
    );
  }

  createDocument(payload: DocumentCreateRequest): Observable<BondDocumentItem> {
    const formData = new FormData();
    formData.append('Filename', payload.filename);
    if (payload.parentElementId) {
      formData.append('ParentElementId', payload.parentElementId);
    }
    if (payload.securityId) {
      formData.append('SecurityId', payload.securityId);
    }
    formData.append('Description', payload.description ?? '');
    formData.append('Type', payload.type);
    formData.append('Source', payload.source);
    formData.append('ModifiedBy', payload.modifiedBy);
    formData.append('File', payload.file);

    return this.http.post<unknown>(this.documentsEndpoint, formData).pipe(
      map((response) => this.toDocument(this.extractDetailRecord(response))),
    );
  }

  updateDocument(id: number, payload: DocumentUpdateRequest): Observable<BondDocumentItem> {
    return this.http.put<unknown>(
      `${this.documentsEndpoint}/${encodeURIComponent(String(id))}`,
      payload,
    ).pipe(
      map((response) => this.toDocument(this.extractDetailRecord(response))),
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`);
  }

  private normalizeBondDetail(response: unknown): BondDetail {
    const record = this.extractDetailRecord(response);

    return {
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      secId: this.toText(this.read(record, ['secId', 'SecId', 'securityId', 'SecurityId'])),
      cusip: this.toText(this.read(record, ['cusip', 'Cusip'])),
      isin: this.toText(this.read(record, ['isin', 'Isin'])),
      location: this.toText(this.read(record, ['location', 'Location'])),
      company: this.toText(this.read(record, ['company', 'Company'])),
      desk: this.toText(this.read(record, ['desk', 'Desk'])),
      book: this.toText(this.read(record, ['book', 'Book'])),
      folder: this.toText(this.read(record, ['folder', 'Folder'])),
      securityType: this.toText(this.read(record, ['securityType', 'SecurityType'])),
      marketType: this.toText(this.read(record, ['marketType', 'MarketType'])),
      doneUser: this.toText(this.read(record, ['doneUser', 'DoneUser'])),
      modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
      settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
      maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      notional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      coupon: this.toNullableNumber(this.read(record, ['coupon', 'Coupon'])),
      indexName: this.toText(this.read(record, ['indexName', 'IndexName'])),
      spread: this.toText(this.read(record, ['spread', 'Spread'])),
      ccy: this.toText(this.read(record, ['ccy', 'Ccy', 'currency', 'Currency'])),
      isNew: this.toBoolean(this.read(record, ['isNew', 'IsNew'])),
      isSale: this.toBoolean(this.read(record, ['isSale', 'IsSale'])),
      bondTypeId: this.toNullableNumber(this.read(record, ['bondTypeId', 'BondTypeId'])),
      bondProceedId: this.toNullableNumber(this.read(record, ['bondProceedId', 'BondProceedId'])),
      bondDocumentationId: this.toNullableNumber(this.read(record, ['bondDocumentationId', 'BondDocumentationId'])),
      bondTypes: this.toSimpleCatalog(this.read(record, ['bondTypes', 'BondTypes'])),
      bondPricingSupplement: this.toText(this.read(record, ['bond_Pricing_Supplement', 'Bond_Pricing_Supplement'])),
      tranche: this.toText(this.read(record, ['tranche', 'Tranche'])),
      comments: this.toText(this.read(record, ['comments', 'Comments'])),
      autoTranche: this.toText(this.read(record, ['autoTranche', 'AutoTranche'])),
      bondGlRepurchase: this.toText(this.read(record, ['bondGlRepurchase', 'BondGlRepurchase'])),
      bondGlRefinance: this.toText(this.read(record, ['bondGlRefinance', 'BondGlRefinance'])),
      us3MlCost: this.toNullableNumber(this.read(record, ['us3MlCost', 'Us3MlCost'])),
      autoUs3MlCost: this.toNullableNumber(this.read(record, ['autoUs3MlCost', 'AutoUs3MlCost'])),
      sofrCost: this.toNullableNumber(this.read(record, ['sofrCost', 'SofrCost'])),
      autoSofrCost: this.toNullableNumber(this.read(record, ['autoSofrCost', 'AutoSofrCost'])),
      issFeeCurrency: this.toText(this.read(record, ['issFeeCurrency', 'IssFeeCurrency'])),
      issFee: this.toNullableNumber(this.read(record, ['issFee', 'IssFee'])),
      bondTheoreticalFee: this.toNullableNumber(this.read(record, ['bondTheoreticalFee', 'BondTheoreticalFee'])),
      autoBondTheoreticalFee: this.toNullableNumber(this.read(record, ['autoBondTheoreticalFee', 'AutoBondTheoreticalFee'])),
      repurcOriginalCost: this.toNullableNumber(this.read(record, ['repurcOriginalCost', 'RepurcOriginalCost'])),
      repurcNewFundingCost: this.toNullableNumber(this.read(record, ['repurcNewFundingCost', 'RepurcNewFundingCost'])),
      genericField1: this.toNullableNumber(this.read(record, ['genericField1', 'GenericField1'])),
      genericField2: this.toNullableNumber(this.read(record, ['genericField2', 'GenericField2'])),
      genericField3: this.toText(this.read(record, ['genericField3', 'GenericField3'])),
      hasLegalDocument: this.toBoolean(this.read(record, ['hasLegalDocument', 'HasLegalDocument'])),
      hasTermSheet: this.toBoolean(this.read(record, ['hasTermSheet', 'HasTermSheet'])),
      hasRta: this.toBoolean(this.read(record, ['hasRTA', 'HasRTA', 'hasRta', 'HasRta'])),
      executedDocsSignatureRemoved: this.toBoolean(
        this.read(record, ['executedDocsSignatureRemoved', 'ExecutedDocsSignatureRemoved']),
      ),
      isPrivatePricingSupplement: this.toBoolean(
        this.read(record, ['isPrivatePricingSupplement', 'IsPrivatePricingSupplement']),
      ),
      investors: this.toInvestors(this.read(record, ['investors', 'Investors'])),
      tradeLead: this.toTradeLeads(this.read(record, ['tradeLead', 'TradeLead'])),
      nonNamedDealers: this.toNonNamedDealers(this.read(record, ['nonNamedDealers', 'NonNamedDealers'])),
      relatedInstruments: this.toRelatedInstruments(this.read(record, ['relatedInstruments', 'RelatedInstruments'])),
      bondTradeBmks: this.toBondTradeBmks(this.read(record, ['bondTradeBmks', 'BondTradeBmks'])),
      documentsAttached: this.toDocuments(this.read(record, ['documentsAttached', 'DocumentsAttached'])),
      notes: this.toNotes(this.read(record, ['notes', 'Notes'])),
    };
  }

  private extractDetailRecord(response: unknown): Record<string, unknown> {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);

    return Object.keys(data).length > 0 ? data : record;
  }

  private toSimpleCatalog(value: unknown): SimpleCatalogOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'typeId', 'TypeId'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
      };
    });
  }

  private toInvestors(value: unknown): BondInvestorItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        name: this.toText(this.read(record, ['name', 'Name', 'investorName', 'InvestorName'])),
        investorTypeName: this.toText(this.read(record, ['investorTypeName', 'InvestorTypeName'])),
        country: this.toText(this.read(record, ['country', 'Country'])),
        region: this.toText(this.read(record, ['region', 'Region'])),
        investorGroupName: this.toText(this.read(record, ['investorGroupName', 'InvestorGroupName'])),
        allocation: this.toNullableNumber(this.read(record, ['allocation', 'Allocation'])),
      };
    });
  }

  private toTradeLeads(value: unknown): TradeLeadItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
        customerId: this.toText(this.read(record, ['customerId', 'CustomerId'])),
        customerName: this.toText(this.read(record, ['customerName', 'CustomerName'])),
        role: this.toText(this.read(record, ['role', 'Role'])),
        arbitrageNotional: this.toNullableNumber(this.read(record, ['arbitrageNotional', 'ArbitrageNotional'])),
        feeLeadColead: this.toNullableNumber(this.read(record, ['feeLeadColead', 'FeeLeadColead'])),
      };
    });
  }

  private toNonNamedDealers(value: unknown): NonNamedDealerItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'dealerId', 'DealerId'])),
        name: this.toText(this.read(record, ['name', 'Name', 'dealerName', 'DealerName', 'customerName', 'CustomerName'])),
      };
    });
  }

  private toBondTradeBmks(value: unknown): BondTradeBmkItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'reofferId', 'ReofferId'])),
        benchmark: this.toText(this.read(record, ['benchmark', 'Benchmark', 'name', 'Name', 'reofferName', 'ReofferName'])),
        tenor: this.toText(this.read(record, ['tenor', 'Tenor'])),
        value: this.toTextLike(this.read(record, ['value', 'Value', 'spread', 'Spread'])),
      };
    });
  }

  private toDocuments(value: unknown): BondDocumentItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => this.toDocument(this.toRecord(entry)));
  }

  private toDocument(record: Record<string, unknown>): BondDocumentItem {
    return {
      id: this.toNumber(this.read(record, ['id', 'Id', 'documentId', 'DocumentId'])),
      description: this.toText(this.read(record, ['description', 'Description', 'name', 'Name', 'documentName', 'DocumentName'])),
      type: this.toText(this.read(record, ['type', 'Type', 'documentType', 'DocumentType'])),
      filename: this.toText(this.read(record, ['filename', 'Filename', 'fileName', 'FileName'])),
      ezShareId: this.toText(this.read(record, ['ezShareId', 'EzShareId', 'eZShareId', 'EZShareId'])),
      ezShareLink: this.toText(this.read(record, ['ezShareLink', 'EzShareLink', 'eZShareLink', 'EZShareLink'])),
    };
  }

  private toNotes(value: unknown): BondNoteItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id', 'noteId', 'NoteId'])),
        note: this.toText(this.read(record, ['note', 'Note', 'text', 'Text', 'comments', 'Comments'])),
        author: this.toText(this.read(record, ['author', 'Author', 'createdBy', 'CreatedBy', 'modifiedBy', 'ModifiedBy'])),
        createdAt: this.toText(this.read(record, ['createdAt', 'CreatedAt', 'dateModified', 'DateModified'])),
      };
    });
  }

  private normalizeRelatedInstruments(value: unknown): RelatedInstrumentItem[] {
    return this.toRelatedInstruments(value);
  }

  private toRelatedInstruments(value: unknown): RelatedInstrumentItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);
      return {
        instrumentType: this.toText(this.read(record, ['instrumentType', 'InstrumentType'])),
        dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
        structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
        tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
        securityId: this.toText(this.read(record, ['securityId', 'SecurityId'])),
        marketType: this.toText(this.read(record, ['marketType', 'MarketType'])),
        payCcyNotional: this.toText(this.read(record, ['payCcyNotional', 'PayCcyNotional'])),
        receiveCcyNotional: this.toText(this.read(record, ['receiveCcyNotional', 'ReceiveCcyNotional'])),
        ccyNotional: this.toText(this.read(record, ['ccyNotional', 'CcyNotional'])),
        coupon: this.toNullableNumber(this.read(record, ['coupon', 'Coupon'])),
        couponPay: this.toNullableNumber(this.read(record, ['couponPay', 'CouponPay'])),
        couponReceive: this.toNullableNumber(this.read(record, ['couponReceive', 'CouponReceive'])),
        tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
        settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
        maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      };
    });
  }

  private normalizeNotes(response: unknown): NoteResponse[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toNoteResponse(entry));
    }

    const record = this.toRecord(response);
    const data = record['data'];

    if (Array.isArray(data)) {
      return data.map((entry) => this.toNoteResponse(entry));
    }

    return [];
  }

  private normalizeNote(response: unknown): NoteResponse {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    return Object.keys(data).length > 0
      ? this.toNoteResponse(data)
      : this.toNoteResponse(record);
  }

  private normalizeGroupedCustomers(response: unknown): DwCustomerGroup[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toDwCustomerGroup(entry));
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return data.map((entry) => this.toDwCustomerGroup(entry));
    }

    return [];
  }

  private toDwCustomerGroup(value: unknown): DwCustomerGroup {
    const record = this.toRecord(value);
    const customersValue = this.read(record, ['customers', 'Customers']);

    return {
      parent: this.toText(this.read(record, ['parent', 'Parent'])) || 'Parent Bank',
      parentLegalName: this.toText(this.read(record, ['parentLegalName', 'ParentLegalName'])),
      customers: Array.isArray(customersValue)
        ? customersValue.map((entry) => this.toDwCustomerItem(entry))
        : [],
    };
  }

  private toDwCustomerItem(value: unknown) {
    const record = this.toRecord(value);

    return {
      id: this.toText(this.read(record, ['id', 'Id', 'customerId', 'CustomerId'])),
      legalName: this.toText(this.read(record, ['legalName', 'LegalName', 'name', 'Name'])),
      parent: this.toText(this.read(record, ['parent', 'Parent'])) || 'Parent Bank',
    };
  }

  private normalizeBondTradeLeadResponse(response: unknown): BondTradeLeadCreateResponse {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    const source = Object.keys(data).length > 0 ? data : record;

    return {
      tradeId: this.toText(this.read(source, ['tradeId', 'TradeId'])),
      customerId: this.toText(this.read(source, ['customerId', 'CustomerId'])),
      customerName: this.toText(this.read(source, ['customerName', 'CustomerName'])),
      role: this.toText(this.read(source, ['role', 'Role'])),
      arbitrageNotional: this.toNullableNumber(this.read(source, ['arbitrageNotional', 'ArbitrageNotional'])),
      feeLeadColead: this.toNullableNumber(this.read(source, ['feeLeadColead', 'FeeLeadColead'])),
    };
  }

  private toNoteResponse(value: unknown): NoteResponse {
    const record = this.toRecord(value);

    return {
      id: this.toNumber(this.read(record, ['id', 'Id'])),
      parentId: this.toText(this.read(record, ['parentId', 'ParentId'])),
      source: this.toText(this.read(record, ['source', 'Source'])),
      description: this.toText(this.read(record, ['description', 'Description'])),
      modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
    };
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (key in record) {
        return record[key];
      }
    }

    return undefined;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toTextLike(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return '';
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = this.toNumber(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
